import * as Sentry from '@sentry/vue';
import type { Router } from 'vue-router';

import { authSession } from '@/auth/useAuth';
import { usePartnerInfoStore } from '@/stores/partnerInfoStore';
import { TLD } from '@/helpers/getLastTld';

import type { AnalyticsEvent, AnalyticsParams } from './events';
import type { TrackingDestination } from './destinations/TrackingDestination';

export interface TrackingContext {
  snapshot(): AnalyticsParams;
}

const MOBILE_QUERY = '(max-width: 480px)';

export class AppTrackingContext implements TrackingContext {
  constructor(private readonly router: Router) {}

  snapshot(): AnalyticsParams {
    const route = this.router.currentRoute.value;
    const { profile } = usePartnerInfoStore();

    return {
      source: String(route.name ?? route.path),
      user_profiles: authSession.value.profiles.join(','),
      partner_id: profile.partner_profile_id || null,
      country: TLD,
      partner_tier: profile.tier,
      device: window.matchMedia?.(MOBILE_QUERY).matches ? 'mobile' : 'desktop',
      app_version: __APP_VERSION__,
    };
  }
}

export class Tracker {
  constructor(
    private readonly context: TrackingContext,
    private readonly destinations: readonly TrackingDestination[],
  ) {}

  track = (event: AnalyticsEvent, params: AnalyticsParams = {}): void => {
    const payload = { ...this.snapshotContext(event), ...params };

    this.destinations.forEach(destination => {
      try {
        destination.deliver(event, payload);
      } catch (error) {
        reportFailure(error, event);
      }
    });
  };

  private snapshotContext(event: AnalyticsEvent): AnalyticsParams {
    try {
      return this.context.snapshot();
    } catch (error) {
      reportFailure(error, event);
      return {};
    }
  }
}

function reportFailure(error: unknown, event: AnalyticsEvent): void {
  Sentry.captureException(error, {
    level: 'warning',
    fingerprint: ['analytics-track-failed'],
    tags: { event },
  });
}
